/**
 * Which Chatwoot workspace this page is looking at.
 *
 * Every BFF route is scoped to `/authoring/api/v1/accounts/:accountId`, and the BFF
 * takes the account from the path ONLY — nothing in a body selects a tenant, and an
 * admin of a different account is a hard 403. So this value is on the critical path of
 * every request the section makes, and it has exactly one authority: the SPA route,
 * `/app/accounts/:accountId/waslo/*`.
 *
 * The store getter is not a second authority. `store/modules/auth.js:55`
 * `getCurrentAccountId` IS `route.params.accountId`, read through vuex-router-sync — one
 * value, two spellings, which cannot disagree. It is here only so a page mounted outside
 * a matched Waslo route (a spec, the render harness) still resolves an id instead of
 * failing on plumbing that has nothing to do with what is being tested.
 *
 * `null` when neither is available. Callers must not paper over that: `wasloUrl()`
 * throws on it, which is the loud failure we want, because a request without the account
 * segment matches no BFF route at all.
 */

import { computed, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';

const positiveInt = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

/** @returns {import('vue').ComputedRef<number|null>} */
export const useWasloAccountId = () => {
  const route = useRoute();
  // Not `useStore()`: it throws when no store is installed, and this fallback exists
  // precisely for the contexts that have not installed one.
  const store = getCurrentInstance()?.proxy?.$store ?? null;

  return computed(
    () =>
      positiveInt(route?.params?.accountId) ??
      positiveInt(store?.getters?.getCurrentAccountId) ??
      null
  );
};

export default useWasloAccountId;
